/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Progress, SelectItem } from '@mainstage/core-components';
import { useApi } from '@mainstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import { useEffect } from 'react';
import useAsync from 'react-use/lib/useAsync';

import { WbSelect } from '@agilelab/plugin-wb-platform';
import { MenuItem } from '@material-ui/core';
import { scaffolderApiRef } from '../../../api';

export const RepoUrlPickerHost = (props: {
  title?: string;
  description?: string;
  host?: string;
  hosts?: string[];
  onChange: (host: string) => void;
  rawErrors: string[];
}) => {
  const { host, hosts, onChange, rawErrors, title, description } = props;
  const scaffolderApi = useApi(scaffolderApiRef);

  const { value: { integrations } = { integrations: [] }, loading } = useAsync(
    async () => {
      return scaffolderApi.getIntegrationsList({
        allowedHosts: hosts ?? [],
      });
    },
  );

  useEffect(() => {
    // If there is no host chosen currently
    if (!host) {
      // Set the first of the allowedHosts option if that available
      if (hosts?.length) {
        onChange(hosts[0]);
        // if there's no hosts provided, fallback to using the first integration
      } else if (integrations?.length) {
        onChange(integrations[0].host);
      }
    }
  }, [hosts, host, onChange, integrations]);

  // If there are no allowedHosts provided, then show all integrations. Otherwise, only show integrations
  // that are provided in the dropdown for the user to choose from.
  const hostsOptions: SelectItem[] = integrations
    ? integrations
        .filter(i => (hosts?.length ? hosts?.includes(i.host) : true))
        .map(i => ({ label: i.title, value: i.host }))
    : [{ label: 'Loading...', value: 'loading' }];

  if (loading) {
    return <Progress />;
  }

  return (
    <>
      <FormControl
        margin="normal"
        required
        error={rawErrors?.length > 0 && !host}
      >
        <WbSelect
          disabled={(hosts && hosts?.length === 1) ?? false}
          label={title ?? 'Host'}
          id="host"
          onChange={e => onChange(e.target.value)}
          value={host}
          data-testid="host-select"
          helperText={
            description ?? 'The host where the repository will be created'
          }
        >
          {hostsOptions.map(hostOption => (
            <MenuItem key={hostOption.value} value={hostOption.value}>
              {hostOption.label}
            </MenuItem>
          ))}
        </WbSelect>
      </FormControl>
    </>
  );
};
